/**
 * Plugin automations (ADR 0025):
 * - on a schedule (the latest moment only, once; remembered on the plugin's page,
 *   so another device does not run it again after syncing);
 * - when a task is completed, or a page is made — only for changes made on this
 *   device while the window is in use, so a sync does not set them off again
 *   on every device.
 */
import { useEffect, useRef } from 'react';
import { latestOccurrence } from '@nexus/core';
import type { InstalledPlugin, PluginEvent } from '@nexus/plugins';
import { loadTasks } from '@nexus/workspace';
import { useStore } from '../db/context';
import { useDescribe } from '../ai/describe';
import { useSyncState } from '../sync/useSync';
import { pluginToast, sandboxFor, usePluginLog, usePlugins } from './runtime';

const RUNS_PROP = 'nexus.plugin.runs';
const EVERY_MS = 60_000;

const active = () => document.visibilityState === 'visible' && document.hasFocus();

export function usePluginAutomations() {
  const store = useStore();
  const describe = useDescribe();
  const plugins = usePlugins();
  const ready = useSyncState((s) => s.authReady && (!s.session || !!s.status.lastSyncAt));
  const withAutomations = (plugins ?? []).filter((p) => p.enabled && p.manifest.automations.length);
  const latest = useRef(withAutomations);
  const describeRef = useRef(describe);
  useEffect(() => {
    latest.current = withAutomations;
    describeRef.current = describe;
  });

  const fire = async (plugin: InstalledPlugin, id: string, args: unknown[]) => {
    try {
      const box = await sandboxFor(store, plugin, describeRef.current);
      const out = await box.run('automation', id, args);
      if (typeof out === 'string' && out.trim()) pluginToast(plugin.manifest.name, out);
    } catch (e) {
      usePluginLog
        .getState()
        .add(plugin.pageId, `${id}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  const fireEvent = (event: PluginEvent, args: unknown[]) => {
    for (const p of latest.current)
      for (const a of p.manifest.automations) if (a.on === event) void fire(p, a.id, args);
  };

  // Scheduled ones.
  useEffect(() => {
    if (!ready) return;
    const run = async () => {
      for (const p of latest.current)
        for (const a of p.manifest.automations) {
          if (a.on !== 'schedule' || !a.schedule) continue;
          const since = new Date(p.updatedAt).toISOString();
          const moment = latestOccurrence({ ...a.schedule, since }, new Date());
          if (!moment) continue;
          const runs = ((await store.getProps(p.pageId))[RUNS_PROP] ?? {}) as Record<
            string,
            string
          >;
          if (runs[a.id] === moment.toISOString()) continue;
          await store.setProp(p.pageId, RUNS_PROP, { ...runs, [a.id]: moment.toISOString() });
          void fire(p, a.id, [{ at: moment.toISOString() }]);
        }
    };
    void run();
    const timer = setInterval(() => void run(), EVERY_MS);
    return () => clearInterval(timer);
    // fire uses refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, ready]);

  // Completed tasks and new pages, made here.
  const anyEvents = withAutomations.some((p) =>
    p.manifest.automations.some((a) => a.on !== 'schedule'),
  );
  useEffect(() => {
    if (!anyEvents) return;
    let done: Set<string> | null = null;
    let pages: Set<string> | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = async () => {
      const tasks = await loadTasks(store);
      const nowDone = new Set(tasks.filter((t) => t.status === 'done').map((t) => t.id));
      const all = await store.listPages();
      const nowPages = new Set(all.map((p) => p.id));
      if (done && pages && active()) {
        for (const t of tasks)
          if (t.status === 'done' && !done.has(t.id))
            fireEvent('task.completed', [
              {
                id: t.id,
                title: t.title,
                pageId: t.pageId,
                projectId: t.projectId,
                doneAt: t.doneAt,
              },
            ]);
        for (const p of all)
          if (!pages.has(p.id))
            fireEvent('page.created', [{ id: p.id, title: p.title, parentId: p.parentId }]);
      }
      done = nowDone;
      pages = nowPages;
    };
    void check();
    const off = store.subscribe(() => {
      clearTimeout(timer);
      timer = setTimeout(() => void check(), 800);
    }, ['tasks', 'pages']);
    return () => {
      clearTimeout(timer);
      off();
    };
    // fireEvent uses refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, anyEvents]);
}
